import React, { useState, useEffect, useRef } from "react";
import {
  Calendar,
  Clock,
  Users,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  FileText,
  Download,
  Printer,
  X,
  Activity,
  BarChart3,
} from "lucide-react";

const DoctorReportGenerator = ({
  isOpen,
  onClose,
  doctorData,
  appointmentsData,
  statsData,
}) => {
  const [reportData, setReportData] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const printRef = useRef();

  useEffect(() => {
    if (isOpen && appointmentsData) {
      generateReportData();
    }
  }, [isOpen, appointmentsData]);

  const generateReportData = () => {
    setIsGenerating(true);

    // Process appointments data for report
    const appointments = Array.isArray(appointmentsData)
      ? appointmentsData
      : appointmentsData?.data || [];

    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();

    // Filter appointments for current month
    const monthlyAppointments = appointments.filter((apt) => {
      const aptDate = new Date(apt.date);
      return (
        aptDate.getMonth() === currentMonth &&
        aptDate.getFullYear() === currentYear
      );
    });

    // Calculate statistics
    const totalAppointments = appointments.length;
    const monthlyTotal = monthlyAppointments.length;
    const completed = appointments.filter(
      (apt) => apt.status === "completed"
    ).length;
    const pending = appointments.filter(
      (apt) => apt.status === "pending"
    ).length;
    const cancelled = appointments.filter(
      (apt) => apt.status === "cancelled"
    ).length;
    const confirmed = appointments.filter(
      (apt) => apt.status === "confirmed"
    ).length;

    // Calculate completion rate
    const completionRate =
      totalAppointments > 0
        ? ((completed / totalAppointments) * 100).toFixed(1)
        : 0;

    // Group by appointment type
    const typeStats = appointments.reduce((acc, apt) => {
      acc[apt.type] = (acc[apt.type] || 0) + 1;
      return acc;
    }, {});

    // Recent appointments (last 5)
    const recentAppointments = appointments
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 5);

    setReportData({
      doctor: doctorData,
      generatedDate: currentDate.toLocaleDateString(),
      period: `${currentDate.toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      })}`,
      stats: {
        total: totalAppointments,
        monthly: monthlyTotal,
        completed,
        pending,
        cancelled,
        confirmed,
        completionRate,
      },
      typeBreakdown: typeStats,
      recentAppointments,
    });

    setIsGenerating(false);
  };

  const handlePrint = () => {
    setIsPrinting(true);

    // Add print styles to the document
    const printStyles = `
      @media print {
        body * {
          visibility: hidden;
        }
        .print-content, .print-content * {
          visibility: visible;
        }
        .print-content {
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
        }
        .no-print {
          display: none !important;
        }
      }
    `;

    const styleSheet = document.createElement("style");
    styleSheet.type = "text/css";
    styleSheet.innerText = printStyles;
    document.head.appendChild(styleSheet);

    // Add print class to the report content
    printRef.current.classList.add("print-content");

    // Trigger print
    window.print();

    // Clean up
    setTimeout(() => {
      document.head.removeChild(styleSheet);
      printRef.current.classList.remove("print-content");
      setIsPrinting(false);
    }, 1000);
  };

  const handleDownload = () => {
    // Create a new window with the report content for download
    const reportContent = printRef.current.innerHTML;
    const fullHTML = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Doctor Report - ${doctorData?.name || "Doctor"}</title>
          <meta charset="UTF-8">
          <style>
            @page {
              margin: 20mm;
              size: A4;
            }
            body {
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 20px;
              background: white;
              color: #000;
              line-height: 1.4;
              font-size: 14px;
            }
            .report-header {
              text-align: center;
              margin-bottom: 30px;
            }
            .report-title {
              font-size: 24px;
              font-weight: bold;
              margin-bottom: 20px;
              color: #000;
            }
            .doctor-info {
              margin-bottom: 20px;
              text-align: left;
            }
            .info-line {
              margin-bottom: 5px;
              font-size: 14px;
            }
            .info-label {
              font-weight: bold;
              display: inline;
            }
            .performance-header {
              color: #2563eb;
              font-weight: bold;
              font-size: 16px;
              margin: 25px 0 15px 0;
            }
            .performance-metrics {
              margin-bottom: 25px;
            }
            .metric-line {
              margin-bottom: 8px;
              font-size: 14px;
            }
            .stats-container {
              display: flex;
              justify-content: space-around;
              margin: 25px 0;
              text-align: center;
            }
            .stat-item {
              flex: 1;
              margin: 0 10px;
            }
            .stat-number {
              font-size: 32px;
              font-weight: bold;
              display: block;
              margin-bottom: 5px;
            }
            .stat-label {
              font-size: 12px;
              font-weight: bold;
              text-transform: uppercase;
            }
            .section-title {
              font-weight: bold;
              font-size: 16px;
              margin: 25px 0 15px 0;
              color: #000;
            }
            .breakdown-item {
              margin-bottom: 5px;
              font-size: 14px;
            }
            .appointments-table {
              width: 100%;
              border-collapse: collapse;
              margin: 15px 0;
            }
            .appointments-table th {
              border: 1px solid #000;
              padding: 8px;
              text-align: left;
              font-weight: bold;
              background: #f5f5f5;
              font-size: 14px;
            }
            .appointments-table td {
              border: 1px solid #000;
              padding: 8px;
              font-size: 14px;
            }
            .footer {
              margin-top: 40px;
              text-align: center;
              font-size: 12px;
              color: #666;
            }
            .footer-line {
              margin-bottom: 5px;
            }
          </style>
        </head>
        <body>
          ${reportContent}
        </body>
      </html>
    `;

    const blob = new Blob([fullHTML], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Doctor_Report_${doctorData?.name || "Unknown"}_${
      new Date().toISOString().split("T")[0]
    }.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div
          className={`p-6 border-b border-gray-200 flex justify-between items-center ${
            isPrinting ? "no-print" : ""
          }`}
        >
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
              <FileText className="w-6 h-6 mr-2 text-blue-600" />
              Doctor Performance Report
            </h2>
            <p className="text-gray-600 mt-1">
              Generated for Dr. {doctorData?.name || "Unknown"}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handlePrint}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
              disabled={isGenerating || isPrinting}
            >
              <Printer className="w-4 h-4" />
              <span>Print</span>
            </button>
            <button
              onClick={handleDownload}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
              disabled={isGenerating}
            >
              <Download className="w-4 h-4" />
              <span>Download</span>
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Report Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isGenerating ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Generating report...</p>
              </div>
            </div>
          ) : reportData ? (
            <div
              ref={printRef}
              className="bg-white"
              style={{
                fontFamily: "Arial, sans-serif",
                fontSize: "14px",
                lineHeight: "1.4",
              }}
            >
              {/* Report Header */}
              <div className="report-header">
                <div
                  className="report-title"
                  style={{
                    fontSize: "24px",
                    fontWeight: "bold",
                    marginBottom: "20px",
                    textAlign: "center",
                  }}
                >
                  Veterinary Practice Report
                </div>

                <div
                  className="doctor-info"
                  style={{ textAlign: "left", marginBottom: "20px" }}
                >
                  <div className="info-line" style={{ marginBottom: "5px" }}>
                    <span className="info-label" style={{ fontWeight: "bold" }}>
                      Doctor:{" "}
                    </span>
                    Dr. {reportData.doctor?.name || "Unknown"}
                  </div>
                  <div className="info-line" style={{ marginBottom: "5px" }}>
                    <span className="info-label" style={{ fontWeight: "bold" }}>
                      Email:{" "}
                    </span>
                    {reportData.doctor?.email || "N/A"}
                  </div>
                  <div className="info-line" style={{ marginBottom: "5px" }}>
                    <span className="info-label" style={{ fontWeight: "bold" }}>
                      Report Period:{" "}
                    </span>
                    {reportData.period}
                  </div>
                  <div className="info-line" style={{ marginBottom: "5px" }}>
                    <span className="info-label" style={{ fontWeight: "bold" }}>
                      Generated:{" "}
                    </span>
                    {reportData.generatedDate}
                  </div>
                </div>
              </div>

              {/* Performance Overview */}
              <div
                className="performance-header"
                style={{
                  color: "#2563eb",
                  fontWeight: "bold",
                  fontSize: "16px",
                  margin: "25px 0 15px 0",
                }}
              >
                Performance Overview
              </div>

              <div
                className="performance-metrics"
                style={{ marginBottom: "25px" }}
              >
                <div className="metric-line" style={{ marginBottom: "8px" }}>
                  Total Appointments Handled: {reportData.stats.total}
                </div>
                <div className="metric-line" style={{ marginBottom: "8px" }}>
                  This Month: {reportData.stats.monthly}
                </div>
                <div className="metric-line" style={{ marginBottom: "8px" }}>
                  Completion Rate: {reportData.stats.completionRate}%
                </div>
                <div className="metric-line" style={{ marginBottom: "8px" }}>
                  Most Common Service:{" "}
                  {Object.entries(reportData.typeBreakdown).length > 0
                    ? Object.entries(reportData.typeBreakdown).sort(
                        ([, a], [, b]) => b - a
                      )[0][0]
                    : "N/A"}
                </div>
              </div>

              {/* Statistics */}
              <div
                className="stats-container"
                style={{
                  display: "flex",
                  justifyContent: "space-around",
                  margin: "25px 0",
                  textAlign: "center",
                }}
              >
                <div
                  className="stat-item"
                  style={{ flex: 1, margin: "0 10px" }}
                >
                  <div
                    className="stat-number"
                    style={{
                      fontSize: "32px",
                      fontWeight: "bold",
                      marginBottom: "5px",
                    }}
                  >
                    {reportData.stats.completed}
                  </div>
                  <div
                    className="stat-label"
                    style={{
                      fontSize: "12px",
                      fontWeight: "bold",
                      textTransform: "uppercase",
                    }}
                  >
                    COMPLETED
                  </div>
                </div>
                <div
                  className="stat-item"
                  style={{ flex: 1, margin: "0 10px" }}
                >
                  <div
                    className="stat-number"
                    style={{
                      fontSize: "32px",
                      fontWeight: "bold",
                      marginBottom: "5px",
                    }}
                  >
                    {reportData.stats.pending}
                  </div>
                  <div
                    className="stat-label"
                    style={{
                      fontSize: "12px",
                      fontWeight: "bold",
                      textTransform: "uppercase",
                    }}
                  >
                    PENDING
                  </div>
                </div>
                <div
                  className="stat-item"
                  style={{ flex: 1, margin: "0 10px" }}
                >
                  <div
                    className="stat-number"
                    style={{
                      fontSize: "32px",
                      fontWeight: "bold",
                      marginBottom: "5px",
                    }}
                  >
                    {reportData.stats.confirmed}
                  </div>
                  <div
                    className="stat-label"
                    style={{
                      fontSize: "12px",
                      fontWeight: "bold",
                      textTransform: "uppercase",
                    }}
                  >
                    CONFIRMED
                  </div>
                </div>
                <div
                  className="stat-item"
                  style={{ flex: 1, margin: "0 10px" }}
                >
                  <div
                    className="stat-number"
                    style={{
                      fontSize: "32px",
                      fontWeight: "bold",
                      marginBottom: "5px",
                    }}
                  >
                    {reportData.stats.cancelled}
                  </div>
                  <div
                    className="stat-label"
                    style={{
                      fontSize: "12px",
                      fontWeight: "bold",
                      textTransform: "uppercase",
                    }}
                  >
                    CANCELLED
                  </div>
                </div>
              </div>

              {/* Service Type Breakdown */}
              <div
                className="section-title"
                style={{
                  fontWeight: "bold",
                  fontSize: "16px",
                  margin: "25px 0 15px 0",
                }}
              >
                Service Type Breakdown
              </div>
              <div>
                {Object.entries(reportData.typeBreakdown).map(
                  ([type, count]) => (
                    <div
                      key={type}
                      className="breakdown-item"
                      style={{
                        marginBottom: "5px",
                        textTransform: "capitalize",
                      }}
                    >
                      {type} {count}
                    </div>
                  )
                )}
              </div>

              {/* Recent Appointments */}
              <div
                className="section-title"
                style={{
                  fontWeight: "bold",
                  fontSize: "16px",
                  margin: "25px 0 15px 0",
                }}
              >
                Recent Appointments
              </div>

              <table
                className="appointments-table"
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  margin: "15px 0",
                }}
              >
                <thead>
                  <tr>
                    <th
                      style={{
                        border: "1px solid #000",
                        padding: "8px",
                        textAlign: "left",
                        fontWeight: "bold",
                        background: "#f5f5f5",
                      }}
                    >
                      Date
                    </th>
                    <th
                      style={{
                        border: "1px solid #000",
                        padding: "8px",
                        textAlign: "left",
                        fontWeight: "bold",
                        background: "#f5f5f5",
                      }}
                    >
                      Pet
                    </th>
                    <th
                      style={{
                        border: "1px solid #000",
                        padding: "8px",
                        textAlign: "left",
                        fontWeight: "bold",
                        background: "#f5f5f5",
                      }}
                    >
                      Owner
                    </th>
                    <th
                      style={{
                        border: "1px solid #000",
                        padding: "8px",
                        textAlign: "left",
                        fontWeight: "bold",
                        background: "#f5f5f5",
                      }}
                    >
                      Type
                    </th>
                    <th
                      style={{
                        border: "1px solid #000",
                        padding: "8px",
                        textAlign: "left",
                        fontWeight: "bold",
                        background: "#f5f5f5",
                      }}
                    >
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.recentAppointments.map((apt, index) => (
                    <tr key={index}>
                      <td style={{ border: "1px solid #000", padding: "8px" }}>
                        {new Date(apt.date).toLocaleDateString()}
                      </td>
                      <td style={{ border: "1px solid #000", padding: "8px" }}>
                        {apt.pets?.name || "Unknown"}
                      </td>
                      <td style={{ border: "1px solid #000", padding: "8px" }}>
                        {apt.pets?.ownerId?.userDetails?.name ||
                          apt.pets?.owner?.name ||
                          "Unknown"}
                      </td>
                      <td
                        style={{
                          border: "1px solid #000",
                          padding: "8px",
                          textTransform: "capitalize",
                        }}
                      >
                        {apt.type}
                      </td>
                      <td
                        style={{
                          border: "1px solid #000",
                          padding: "8px",
                          textTransform: "capitalize",
                        }}
                      >
                        {apt.status}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Footer */}
              <div
                className="footer"
                style={{
                  marginTop: "40px",
                  textAlign: "center",
                  fontSize: "12px",
                  color: "#666",
                }}
              >
                <div className="footer-line" style={{ marginBottom: "5px" }}>
                  This report was automatically generated on{" "}
                  {reportData.generatedDate}
                </div>
                <div className="footer-line">
                  Veterinary Practice Management System | Confidential Document
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default DoctorReportGenerator;
